import {
  alpha,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Button, Record, useNotify } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  DriverDailyLogRecapResult,
  EventApiFactory,
  HosLogsApiFactory,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import TimeField from "../../../common/data-grid-fields/TimeField";
import "./CycleRecapView.css";
import { Refresh } from "@material-ui/icons";

interface Props {
  driverId: number;
  logDate: Date;
  onClose: () => void;
  open: boolean;
  isMaster: boolean;
}

const useStyles = makeStyles<Theme>((theme) => ({
  currentDay: {
    backgroundColor: alpha(theme.palette.success.main, 0.2),
  },
  cycleBreak: {
    "& td": {
      textAlign: "center",
    },
  },
}));

export const CycleRecapView = ({
  driverId,
  logDate,
  onClose,
  open,
  isMaster,
}: Props) => {
  const [data, setData] = useState<DriverDailyLogRecapResult>();
  const [loading, setLoading] = useState<boolean>(true);
  const axios = useAxiosInstance();
  const classes = useStyles();
  const notify = useNotify();

  useEffect(() => {
    if (!axios) {
      return;
    }

    if (!open) {
      return;
    }

    setLoading(true);
    HosLogsApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppHoslogsDriverdailylogrecapGet(driverId, logDate.toISOString())
      .then((response) => {
        setData(response.data);
      })
      .catch((e) => {
        console.error(handleAxiosError(e));
        notify(handleAxiosError(e), { type: "error" });
        onClose();
      })
      .finally(() => setLoading(false));
  }, [driverId, logDate, axios, onClose, open, notify]);

  const handleRebuildFromEvent = useCallback(() => {
    if (!axios) {
      return;
    }

    if (!open) {
      return;
    }

    if (!data?.eventId) {
      return;
    }

    setLoading(true);
    EventApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppEventRebuildcyclefromeventPost({ eventId: data.eventId })
      .catch((e) => {
        console.error(handleAxiosError(e));
        onClose();
      });
  }, [axios, data, onClose, open]);

  const handleRebuildCycle = useCallback(() => {
    if (!axios) {
      return;
    }

    if (!open) {
      return;
    }

    if (!data?.result?.cycleId) {
      return;
    }

    setLoading(true);
    EventApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppEventRebuildcyclePost({ cycleId: data.result.cycleId })
      .catch((e) => {
        console.error(handleAxiosError(e));
        onClose();
      });
  }, [axios, data, onClose, open]);

  if (loading) {
    return (
      <div className="recap-cycle">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="recap-cycle">
      <div className="recap-cycle-header">
        {data?.result?.days?.length ? (
          <div className="recap-cycle-info">
            <div className="recap-cycle-label">Cycle start</div>
            <div className="recap-cycle-value">
              {format(parseISO(data.result.cycleStart!), "eee, MMM do yyyy")}
            </div>
            <div className="recap-cycle-label">Cycle end</div>
            <div className="recap-cycle-value">
              {format(parseISO(data.result.cycleEnd!), "eee, MMM do yyyy")}
            </div>
          </div>
        ) : null}

        {!data?.result?.days?.length ? (
          <div className="recap-cycle-info">
            <Typography variant="body1">No active cycles found</Typography>
          </div>
        ) : null}

        {isMaster && data?.result?.days?.length ? (
          <div className="recap-cycle-command">
            <Button label="Refresh" onClick={handleRebuildCycle}>
              <Refresh />
            </Button>
          </div>
        ) : isMaster && data?.eventId ? (
          <div className="recap-cycle-command">
            <Button label="Refresh" onClick={handleRebuildFromEvent}>
              <Refresh />
            </Button>
          </div>
        ) : null}
      </div>
      {data?.result?.days?.length ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body1">Day</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Worked</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Cycle</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Remaining</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">Gained</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.result.days.map((day) => (
                <TableRow
                  key={day.date}
                  className={day.isCurrentDay ? classes.currentDay : undefined}>
                  <TableCell>{format(parseISO(day.date!), "eee do")}</TableCell>
                  <TableCell>
                    <TimeField
                      record={day as Record}
                      inputType="minutes"
                      outputType="short"
                      source="workingMinutes"
                    />
                  </TableCell>
                  <TableCell>
                    <TimeField
                      record={day as Record}
                      inputType="minutes"
                      outputType="short"
                      source="totalWorkingMinutes"
                    />
                  </TableCell>
                  <TableCell>
                    <TimeField
                      record={day as Record}
                      inputType="minutes"
                      outputType="short"
                      source="remainingWorkingMinutes"
                    />
                  </TableCell>
                  <TableCell>
                    {day.minutesGainedAfterToday ? (
                      <TimeField
                        record={day as Record}
                        inputType="minutes"
                        outputType="short"
                        source="minutesGainedAfterToday"
                      />
                    ) : (
                      "00:00"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </div>
  );
};
